import i18n from 'i18next';
import { Dispatch } from 'redux';
import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest
} from 'src/store/api-handler';
import { GradeDto, GradeEditDto } from 'src/store/grades/grade';
import { alertError, alertSuccess } from '../alerts/alerts.actions';
import { requestBegin, requestEnd } from '../requests/requests.actions';

export enum GradeTypes {
  FetchAll = 'grades/FetchAll',
  Add = 'grades/Add',
  Update = 'grades/Update',
  Delete = 'grades/Delete',
  Failure = 'grades/Failure'
}

export type GradeAction =
  | { type: GradeTypes.FetchAll; grades: GradeDto[] }
  | { type: GradeTypes.Add; grade: GradeDto }
  | { type: GradeTypes.Update; grade: GradeDto }
  | { type: GradeTypes.Delete; grade: GradeDto }
  | { type: GradeTypes.Failure; errors: any };

export const actionCreators = {
  fetchGrades: (grades: GradeDto[]): GradeAction => ({
    type: GradeTypes.FetchAll,
    grades
  }),
  addGrade: (grade: GradeDto): GradeAction => ({
    type: GradeTypes.Add,
    grade
  }),
  updateGrade: (grade: GradeDto): GradeAction => ({
    type: GradeTypes.Update,
    grade
  }),
  deleteGrade: (grade: GradeDto): GradeAction => ({
    type: GradeTypes.Delete,
    grade
  }),
  gradeRequestFailure: (errors: any): GradeAction => ({
    type: GradeTypes.Failure,
    errors
  })
};

export const fetchGrades = () => {
  return async (dispatch: Dispatch) => {
    dispatch(requestBegin(GradeTypes.FetchAll));
    const { ok, data, error } = await getRequest<GradeDto[]>('/grades');
    dispatch(requestEnd(GradeTypes.FetchAll));
    return ok
      ? dispatch(actionCreators.fetchGrades(data))
      : dispatch(actionCreators.gradeRequestFailure(error));
  };
};

export const createGrade = (grade: GradeEditDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await postRequest('/grades', grade);

    if (ok) {
      return dispatch(alertSuccess(i18n.t('grade.created')));
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('create.failed')));
    return dispatch(actionCreators.gradeRequestFailure(error));
  };
};

export const editGrade = (id: number, grade: GradeEditDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await putRequest(`/grades/${id}`, grade);

    if (ok) {
      return dispatch(alertSuccess(i18n.t('grade.updated')));
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('save.failed')));
    return dispatch(actionCreators.gradeRequestFailure(error));
  };
};

export const editGradeCorrections = (id: number, grade: GradeEditDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await putRequest(`/grades/${id}/corrections`, grade);

    if (ok) {
      return dispatch(alertSuccess(i18n.t('grade.updated')));
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('save.failed')));
    return dispatch(actionCreators.gradeRequestFailure(error));
  };
};

export const deleteGrade = (id: number, grade: GradeDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await deleteRequest(`/grades/${id}`, grade);

    if (ok) {
      dispatch(alertSuccess(i18n.t('grade.deleted')));
      return dispatch(actionCreators.deleteGrade(grade));
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('remove.failed')));
    return dispatch(actionCreators.gradeRequestFailure(error));
  };
};
